const ROOT_SELECTOR = '[data-filterable-listing]';
const FORM_SELECTOR = '[data-filterable-listing-form]';
const ACTION_LINK_SELECTOR = '[data-filterable-listing-link]';
const PAGINATION_LINK_SELECTOR = '.pagination-wrapper a.page-link';
const CLIENT_PAGINATION_SELECTOR = '[data-client-pagination]';
const CLIENT_ITEMS_ATTR = 'data-client-items-per-page';
const RESULTS_SELECTOR = '[data-filterable-listing-results]';
const EMPTY_SELECTOR = '[data-filterable-listing-empty]';
const SELECTED_FILTERS_SELECTOR = '[data-selected-filters]';
const LOADING_CLASS = 'is-loading';

const readPositiveInt = (raw: string | null | undefined) => {
  if (!raw) {
    return 0;
  }

  const n = parseInt(raw, 10);

  return Number.isFinite(n) && n > 0 ? n : 0;
};

const getClientPageSize = (root: HTMLElement) => readPositiveInt(root.getAttribute(CLIENT_ITEMS_ATTR));

const getCurrentPageFromUrl = (url: URL) => {
  const n = parseInt(url.searchParams.get('page') ?? '1', 10);

  return Number.isFinite(n) && n > 0 ? n : 1;
};

const syncUrlPageParam = (url: URL, page: number) => {
  if (page <= 1) {
    url.searchParams.delete('page');
  } else {
    url.searchParams.set('page', String(page));
  }
};

const isModifiedClick = (event: MouseEvent) =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

const getListingRoots = (scope: ParentNode = document) =>
  Array.from(scope.querySelectorAll<HTMLElement>(ROOT_SELECTOR));

const isClientListingRoot = (root: HTMLElement) => root.hasAttribute('data-filterable-listing-client');

const setLoadingState = (roots: HTMLElement[], isLoading: boolean) => {
  roots.forEach((root) => {
    root.classList.toggle(LOADING_CLASS, isLoading);
    if (isLoading) {
      root.setAttribute('aria-busy', 'true');
    } else {
      root.removeAttribute('aria-busy');
    }
  });
};

const getOpenFilterGroups = (form: ParentNode) =>
  Array.from(form.querySelectorAll<HTMLDetailsElement>('details[data-filter-group][open]'))
    .map((group) => group.dataset.filterGroup ?? '')
    .filter((groupName) => groupName.length > 0);

const applyOpenFilterGroups = (scope: ParentNode, openGroups: string[]) => {
  const openGroupSet = new Set(openGroups);

  scope.querySelectorAll<HTMLDetailsElement>('details[data-filter-group]').forEach((group) => {
    const groupName = group.dataset.filterGroup ?? '';
    group.open = openGroupSet.has(groupName);
  });
};

const buildUrlFromForm = (form: HTMLFormElement) => {
  const nextUrl = new URL(window.location.href);
  const formData = new FormData(form);
  const managedKeys = new Set<string>();

  Array.from(form.elements).forEach((element) => {
    if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) {
      if (element.name) {
        managedKeys.add(element.name);
      }
    }
  });

  managedKeys.forEach((key) => nextUrl.searchParams.delete(key));

  formData.forEach((value, key) => {
    const stringValue = String(value).trim();
    if (stringValue) {
      nextUrl.searchParams.append(key, stringValue);
    }
  });

  return nextUrl;
};

const getSelectedValues = (url: URL, key: string) =>
  Array.from(new Set(url.searchParams.getAll(key)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter((value) => value.length > 0)));

const parseCardValues = (value: string | null) =>
  (value ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

const buildUrlWithValues = (url: URL, key: string, values: string[]) => {
  url.searchParams.delete(key);
  values.forEach((value) => url.searchParams.append(key, value));
};

const buildRemoveFilterUrl = (url: URL, key: string, valueToRemove: string) => {
  const nextUrl = new URL(url.toString());
  nextUrl.searchParams.delete('page');

  const remainingValues = getSelectedValues(nextUrl, key)
    .filter((value) => value !== valueToRemove);

  buildUrlWithValues(nextUrl, key, remainingValues);

  return nextUrl;
};

const buildClearFiltersUrl = (url: URL) => {
  const nextUrl = new URL(url.toString());
  nextUrl.searchParams.delete('page');
  nextUrl.searchParams.delete('category');
  nextUrl.searchParams.delete('tag');

  return nextUrl;
};

const syncSelectedFilters = (scope: ParentNode, url: URL) => {
  const container = scope.querySelector<HTMLElement>(SELECTED_FILTERS_SELECTOR);
  if (!container) {
    return;
  }

  const selectedInputs = Array.from(scope.querySelectorAll<HTMLInputElement>(`${FORM_SELECTOR} input[type="checkbox"]:checked`));
  container.replaceChildren();

  if (!selectedInputs.length) {
    container.hidden = true;
    return;
  }

  selectedInputs.forEach((input) => {
    const optionLabel = input.closest('label')?.querySelector<HTMLElement>('.text')?.textContent?.trim() ?? input.value;
    const pill = document.createElement('a');
    pill.className = 'selected-filter';
    pill.dataset.filterableListingLink = '';
    pill.href = buildRemoveFilterUrl(url, input.name, input.value).toString();

    const label = document.createElement('span');
    label.className = 'selected-filter-label';
    label.textContent = optionLabel;

    const remove = document.createElement('span');
    remove.className = 'selected-filter-remove';
    remove.setAttribute('aria-hidden', 'true');
    remove.textContent = '×';

    const assistive = document.createElement('span');
    assistive.className = 'visually-hidden';
    assistive.textContent = `Remove ${optionLabel} filter`;

    pill.append(label, remove, assistive);
    container.append(pill);
  });

  const clearAll = document.createElement('a');
  clearAll.className = 'selected-filters-clear';
  clearAll.dataset.filterableListingLink = '';
  clearAll.href = buildClearFiltersUrl(url).toString();
  clearAll.textContent = 'Clear all';

  container.append(clearAll);
  container.hidden = false;
};

const syncFormFromUrl = (form: HTMLFormElement, url: URL) => {
  const selectedCategories = new Set(getSelectedValues(url, 'category'));
  const selectedTags = new Set(getSelectedValues(url, 'tag'));

  form.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((input) => {
    let isSelected = input.checked;

    if (input.name === 'category') {
      isSelected = selectedCategories.has(input.value);
      input.checked = isSelected;
    }

    if (input.name === 'tag') {
      isSelected = selectedTags.has(input.value);
      input.checked = isSelected;
    }

    input.closest('label')?.classList.toggle('is-selected', isSelected);
  });
};

const buildPaginationSegments = (
  pageCount: number,
  currentPage: number,
  adjacentCount: number,
  startCount: number,
  endCount: number
): Array<number | 'ellipsis'> => {
  const pages = new Set<number>();
  pages.add(1);
  pages.add(pageCount);

  for (let n = currentPage - adjacentCount; n <= currentPage + adjacentCount; n += 1) {
    if (n >= 1 && n <= pageCount) {
      pages.add(n);
    }
  }

  for (let n = 1; n <= startCount; n += 1) {
    if (n <= pageCount) {
      pages.add(n);
    }
  }

  for (let n = pageCount - endCount + 1; n <= pageCount; n += 1) {
    if (n >= 1) {
      pages.add(n);
    }
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const segments: Array<number | 'ellipsis'> = [];

  sorted.forEach((num, index) => {
    if (index > 0 && num - sorted[index - 1] > 1) {
      segments.push('ellipsis');
    }

    segments.push(num);
  });

  return segments;
};

const renderClientPagination = (root: HTMLElement, urlForLinks: URL, pageSize: number, matchedCards: HTMLElement[]) => {
  const host = root.querySelector<HTMLElement>(CLIENT_PAGINATION_SELECTOR);
  if (!host) {
    return;
  }

  const total = matchedCards.length;

  if (pageSize <= 0 || total === 0) {
    host.hidden = true;
    host.innerHTML = '';

    return;
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  if (pageCount <= 1) {
    host.hidden = true;
    host.innerHTML = '';

    return;
  }

  let currentPage = getCurrentPageFromUrl(urlForLinks);
  currentPage = Math.min(Math.max(1, currentPage), pageCount);

  const nav = document.createElement('nav');
  nav.setAttribute('aria-label', 'Pagination');
  nav.dataset.ktcSearchExclude = '';

  const ul = document.createElement('ul');
  ul.className = 'pagination';

  const hrefForPage = (pageNum: number) => {
    const next = new URL(urlForLinks.toString());
    syncUrlPageParam(next, pageNum);

    return next.pathname + next.search;
  };

  const appendPageControl = (label: string, pageNum: number, options?: { disabled?: boolean; active?: boolean }) => {
    const li = document.createElement('li');
    li.className = 'page-item';

    if (options?.active) {
      li.classList.add('active');
      li.setAttribute('aria-current', 'page');
      const span = document.createElement('span');
      span.className = 'page-link';
      span.textContent = label;
      li.append(span);
    } else if (options?.disabled) {
      li.classList.add('disabled');
      const span = document.createElement('span');
      span.className = 'page-link';
      span.textContent = label;
      li.append(span);
    } else {
      const a = document.createElement('a');
      a.className = 'page-link';
      a.href = hrefForPage(pageNum);
      a.textContent = label;
      li.append(a);
    }

    ul.append(li);
  };

  appendPageControl('‹', currentPage - 1, { disabled: currentPage <= 1 });

  const segments = buildPaginationSegments(pageCount, currentPage, 1, 2, 2);

  segments.forEach((segment) => {
    if (segment === 'ellipsis') {
      appendPageControl('…', currentPage, { disabled: true });
    } else if (segment === currentPage) {
      appendPageControl(String(segment), segment, { active: true });
    } else {
      appendPageControl(String(segment), segment);
    }
  });

  appendPageControl('›', currentPage + 1, { disabled: currentPage >= pageCount });

  nav.append(ul);
  host.innerHTML = '';
  host.append(nav);
  host.hidden = false;
};

const applyClientListingState = (root: HTMLElement, nextUrl: URL, historyMode: 'push' | 'replace') => {
  const form = root.querySelector<HTMLFormElement>(FORM_SELECTOR);
  const results = root.querySelector<HTMLElement>(RESULTS_SELECTOR);
  const emptyState = root.querySelector<HTMLElement>(EMPTY_SELECTOR);

  if (!form || !results) {
    return;
  }

  syncFormFromUrl(form, nextUrl);
  syncSelectedFilters(root, nextUrl);

  const selectedCategories = new Set(getSelectedValues(nextUrl, 'category'));
  const selectedTags = new Set(getSelectedValues(nextUrl, 'tag'));
  const pageSize = getClientPageSize(root);

  const cards = Array.from(results.querySelectorAll<HTMLElement>('article'));

  cards.forEach((card) => {
    const cardCategories = parseCardValues(card.dataset.listingCategories ?? null);
    const cardTags = parseCardValues(card.dataset.listingTags ?? null);
    const matchesCategory = selectedCategories.size === 0 || cardCategories.some((value) => selectedCategories.has(value));
    const matchesTag = selectedTags.size === 0 || cardTags.some((value) => selectedTags.has(value));
    const matchesFilter = matchesCategory && matchesTag;

    card.dataset.listingFilterMatch = matchesFilter ? '1' : '0';
  });

  const matchedCards = cards.filter((card) => card.dataset.listingFilterMatch === '1');
  let visibleCount = matchedCards.length;

  let effectiveUrl = new URL(nextUrl.toString());
  let effectiveHistoryMode = historyMode;

  if (pageSize > 0 && matchedCards.length > 0) {
    const pageCount = Math.ceil(matchedCards.length / pageSize);
    let page = getCurrentPageFromUrl(effectiveUrl);

    if (page > pageCount) {
      page = pageCount;
      syncUrlPageParam(effectiveUrl, page);
      effectiveHistoryMode = 'replace';
    }

    matchedCards.forEach((card, index) => {
      const pageIndex = Math.floor(index / pageSize) + 1;
      card.hidden = pageIndex !== page;
    });

    cards.forEach((card) => {
      if (card.dataset.listingFilterMatch !== '1') {
        card.hidden = true;
      }
    });

    renderClientPagination(root, effectiveUrl, pageSize, matchedCards);
  } else {
    cards.forEach((card) => {
      card.hidden = card.dataset.listingFilterMatch !== '1';
    });

    const host = root.querySelector<HTMLElement>(CLIENT_PAGINATION_SELECTOR);
    if (host) {
      host.hidden = true;
      host.innerHTML = '';
    }
  }

  results.hidden = visibleCount === 0;
  if (emptyState) {
    emptyState.hidden = visibleCount > 0;
  }

  if (effectiveHistoryMode === 'push') {
    window.history.pushState({}, '', effectiveUrl);
  } else {
    window.history.replaceState({}, '', effectiveUrl);
  }
};

const replaceListingsFromDocument = (nextDocument: Document, openGroups: string[]) => {
  const currentRoots = getListingRoots();
  const nextRoots = getListingRoots(nextDocument);

  currentRoots.forEach((root, index) => {
    const nextRoot = nextRoots[index];
    if (nextRoot) {
      applyOpenFilterGroups(nextRoot, openGroups);
      root.replaceWith(nextRoot);
    }
  });
};

const LISTING_BODY_SELECTOR = '[data-filterable-listing-body]';

const getListingConfig = (root: HTMLElement): unknown | null => {
  const configEl = root.querySelector<HTMLScriptElement>('script[data-listing-config]');
  if (!configEl || !configEl.textContent) {
    return null;
  }
  try {
    return JSON.parse(configEl.textContent);
  } catch {
    return null;
  }
};

const refreshListingFragment = async (
  root: HTMLElement,
  endpoint: string,
  config: unknown,
  nextUrl: URL,
  historyMode: 'push' | 'replace',
  openGroups: string[]
) => {
  const body = root.querySelector<HTMLElement>(LISTING_BODY_SELECTOR);
  if (!body) {
    // Body wrapper missing — fall back to full-page navigation rather than silent failure.
    window.location.assign(nextUrl.toString());
    return;
  }

  setLoadingState([root], true);
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        config,
        pagePath: window.location.pathname,
        queryString: nextUrl.search
      })
    });

    if (!response.ok) {
      throw new Error(`Listing fragment refresh failed: ${response.status}`);
    }

    const html = await response.text();
    body.innerHTML = html;
    applyOpenFilterGroups(body, openGroups);

    if (historyMode === 'push') {
      window.history.pushState({}, '', nextUrl);
    } else {
      window.history.replaceState({}, '', nextUrl);
    }
  } catch (error) {
    console.error(error);
    window.location.assign(nextUrl.toString());
  } finally {
    setLoadingState([root], false);
  }
};

const refreshListings = async (nextUrl: URL, historyMode: 'push' | 'replace', openGroups: string[] = []) => {
  const roots = getListingRoots();
  if (!roots.length) {
    return;
  }

  // Prefer the per-widget AJAX fragment endpoint when configured. This avoids round-tripping
  // the entire page document just to swap a listing, which used to send back ~60KB of header,
  // navigation, and footer markup on every page click.
  const endpointRoots = roots.filter((root) => root.hasAttribute('data-listing-endpoint') && getListingConfig(root) !== null);
  if (endpointRoots.length === roots.length && endpointRoots.length > 0) {
    await Promise.all(endpointRoots.map((root) => {
      const endpoint = root.getAttribute('data-listing-endpoint') ?? '';
      const config = getListingConfig(root);
      return refreshListingFragment(root, endpoint, config, nextUrl, historyMode, openGroups);
    }));
    return;
  }

  // Fallback: full-page document fetch + DOM swap (used by any listing widgets that have not
  // been migrated to the fragment endpoint yet).
  setLoadingState(roots, true);

  try {
    const response = await fetch(nextUrl.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Listing refresh failed: ${response.status}`);
    }

    const html = await response.text();
    const nextDocument = new DOMParser().parseFromString(html, 'text/html');
    replaceListingsFromDocument(nextDocument, openGroups);

    if (historyMode === 'push') {
      window.history.pushState({}, '', nextUrl);
    } else {
      window.history.replaceState({}, '', nextUrl);
    }
  } catch (error) {
    console.error(error);
    window.location.assign(nextUrl.toString());
  } finally {
    setLoadingState(getListingRoots(), false);
  }
};

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') {
    return;
  }

  const form = target.closest<HTMLFormElement>(FORM_SELECTOR);
  if (!form) {
    return;
  }

  const root = form.closest<HTMLElement>(ROOT_SELECTOR);
  if (root && isClientListingRoot(root)) {
    applyClientListingState(root, buildUrlFromForm(form), 'push');
    return;
  }

  void refreshListings(buildUrlFromForm(form), 'push', getOpenFilterGroups(form));
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) {
    return;
  }

  const root = form.closest<HTMLElement>(ROOT_SELECTOR);
  if (root && isClientListingRoot(root)) {
    event.preventDefault();
    applyClientListingState(root, buildUrlFromForm(form), 'push');
    return;
  }

  event.preventDefault();
  void refreshListings(buildUrlFromForm(form), 'push', getOpenFilterGroups(form));
});

document.addEventListener('click', (event) => {
  if (!(event instanceof MouseEvent) || isModifiedClick(event)) {
    return;
  }

  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }

  const link = target.closest<HTMLAnchorElement>(`${PAGINATION_LINK_SELECTOR}, ${ACTION_LINK_SELECTOR}`);
  if (!link || !link.closest(ROOT_SELECTOR)) {
    return;
  }

  event.preventDefault();

  const parentRoot = link.closest<HTMLElement>(ROOT_SELECTOR);
  if (parentRoot && isClientListingRoot(parentRoot)) {
    applyClientListingState(parentRoot, new URL(link.href, window.location.origin), 'push');
    return;
  }

  void refreshListings(
    new URL(link.href, window.location.origin),
    'push',
    parentRoot ? getOpenFilterGroups(parentRoot) : []
  );
});

window.addEventListener('popstate', () => {
  const currentUrl = new URL(window.location.href);
  const roots = getListingRoots();

  roots.filter(isClientListingRoot).forEach((root) => {
    applyClientListingState(root, currentUrl, 'replace');
  });

  const serverRoot = roots.find((root) => !isClientListingRoot(root));
  if (serverRoot) {
    void refreshListings(currentUrl, 'replace', getOpenFilterGroups(serverRoot));
  }
});

getListingRoots().filter(isClientListingRoot).forEach((root) => {
  applyClientListingState(root, new URL(window.location.href), 'replace');
});
